/**
 * Typography Provider — Susurro Web
 *
 * Drives the app typeface with the SAME mechanism as the fleet's font campaign:
 * a bundled, self-contained BRAND font (Nunito) that is always present, plus an
 * OPTIONAL runtime override (`--font-user`) for a catalog font. "Reset to default"
 * clears the override and returns to the bundled brand with ZERO dependency on the
 * catalog or the network.
 *
 * Susurro is guest-first / accountless: there is one local identity and no synced
 * source of truth, so this always drives the GUEST path of `resolveDesiredFontId`
 * (cache-only, remote ignored). The choice persists per-device in localStorage,
 * keyed by a stable visitor id — the same anti-leak cache keying as the canon, so
 * the pure resolution logic is genuinely wired here (not just unit-tested).
 */

import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";

import { BRAND_FONT } from "@/config/brand-font";
import { type CatalogFont, findCatalogFont, FONT_CATALOG } from "@/config/font-catalog";
import {
  type CachedFont,
  readCachedFont,
  resolveAvailableFontId,
  resolveDesiredFontId,
  writeCachedFont,
} from "@/lib/typography/font-preference";

/**
 * Accountless single identity. The per-account cache keying still applies (guest
 * path resolves from its OWN cache and ignores any synced value), matching the
 * canon's `VISITOR_ID`.
 */
const VISITOR_ID = "visitor";

interface TypographyContextValue {
  /**
   * The EFFECTIVE font id (drives the selected state in the picker); `null` = the
   * app's brand default — which is ALSO what an unavailable chosen font resolves
   * to, so the default card shows as selected when the chosen font is gone.
   */
  selectedFontId: () => string | null;
  /** True when the device's chosen font is no longer in the available option set. */
  isSelectedUnavailable: () => boolean;
  /** Display name of the chosen-but-unavailable font (for the UI notice), else null. */
  unavailableFontName: () => string | null;
  /** The resolved display family name, or null when on the brand default. */
  fontFamilyName: () => string | null;
  /** Human label of the app's bundled brand font (the reset target). */
  brandLabel: string;
  /** The additive catalog of selectable fonts. */
  catalog: readonly CatalogFont[];
  /** Apply + persist a catalog font for this device (live, no reload). */
  setFont: (font: CachedFont) => void;
  /** Reset to the bundled brand font (clears the override; catalog-independent). */
  resetFont: () => void;
}

const TypographyContext = createContext<TypographyContextValue>();

/** Best-effort access to localStorage (unavailable in some private-mode webviews). */
function getStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/**
 * Applies (or clears) the runtime font override on <html>. Clearing it falls the
 * `--font-*` role tokens straight back to the bundled brand font (see global.css)
 * — the reset path with zero dependency on the catalog.
 */
function applyFontOverride(name: string | null): void {
  const root = document.documentElement;
  if (name) {
    // Keep a generic fallback chain so that if the chosen face fails to load
    // (offline, CDN hiccup) text stays readable instead of vanishing.
    root.style.setProperty("--font-user", `'${name}', ui-sans-serif, system-ui, sans-serif`);
  } else {
    root.style.removeProperty("--font-user");
  }
}

/**
 * Lazily fetches a catalog font's stylesheet the FIRST time it is selected. System
 * faces (no `stylesheetUrl`) and the brand never touch the network. Idempotent: the
 * `<link>` is de-duped by id, so re-selecting never re-appends.
 */
function ensureFontLoaded(id: string): void {
  const entry = findCatalogFont(id);
  if (!entry?.stylesheetUrl) {
    return;
  }
  const linkId = `font-catalog-${entry.id}`;
  if (document.querySelector(`#${linkId}`)) {
    return;
  }
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = entry.stylesheetUrl;
  document.head.append(link);
}

export const TypographyProvider: ParentComponent = (props) => {
  const storage = getStorage();

  // The device's chosen font (null = bundled brand). Seeded from the per-account
  // cache for an instant, catalog-independent boot.
  const [selection, setSelection] = createSignal<CachedFont | null>(
    readCachedFont(storage, VISITOR_ID),
  );

  // ── Effective font id (brand default = null) ────────────────────────────────
  // Always the guest path: cache-only, synced value ignored (anti-leak). This is
  // the canon's pure resolver, wired to Susurro's single local identity.
  const desiredFontId = createMemo<string | null>(() => {
    const { fontId } = resolveDesiredFontId({
      isGuest: true,
      preferencesLoaded: true,
      remoteFontId: null,
      cachedFontId: selection()?.id ?? null,
      pendingFontId: undefined,
    });
    return fontId;
  });

  // ── Graceful availability (v2) ──────────────────────────────────────────────
  // Susurro's "catalog" is the static bundled option set (system faces +
  // on-demand Fontsource in `FONT_CATALOG`): the set is resolved as soon as the
  // app code runs → `catalogLoaded` is constantly true, and `familyFound` is a
  // membership check against that set. A cached id that is no longer offered
  // (catalog curation changed between releases) degrades to the bundled brand —
  // never a dead face — and the UI is told via `isSelectedUnavailable`.
  const availability = createMemo(() =>
    resolveAvailableFontId({
      desiredFontId: desiredFontId(),
      familyFound: findCatalogFont(desiredFontId()) !== undefined,
      catalogLoaded: true,
    }),
  );
  /** Effective font id to apply (brand `null` when the desired one is unavailable). */
  const effectiveFontId = (): string | null => availability().fontId;

  // The display family name to apply (from the cached selection), or null = brand.
  const effectiveName = createMemo<string | null>(() => {
    const id = effectiveFontId();
    if (!id) {
      return null; // brand default (or unavailable → fell back) — clears the override
    }
    const sel = selection();
    return sel?.id === id ? sel.name : null;
  });

  // Apply the effective font instantly and whenever it changes (also on boot).
  // Loading the stylesheet keys off the EFFECTIVE id, so a persisted Fontsource
  // choice is fetched again after a reload and a dead face is never downloaded.
  createEffect(() => {
    const id = effectiveFontId();
    if (id) {
      ensureFontLoaded(id);
    }
    applyFontOverride(effectiveName());
  });

  const setFont = (font: CachedFont): void => {
    // Best-effort download of the catalog face (system faces / brand need nothing).
    ensureFontLoaded(font.id);
    // Persist per-device (instant boot next time) and apply live — no reload.
    writeCachedFont(storage, VISITOR_ID, font);
    applyFontOverride(font.name);
    setSelection(font);
  };

  const resetFont = (): void => {
    // Clear the cache and the override → the bundled brand font, catalog-independent.
    writeCachedFont(storage, VISITOR_ID, null);
    applyFontOverride(null);
    setSelection(null);
  };

  return (
    <TypographyContext.Provider
      value={{
        selectedFontId: effectiveFontId,
        isSelectedUnavailable: () => availability().unavailable,
        unavailableFontName: () => {
          if (!availability().unavailable) {
            return null;
          }
          // Non-destructive fallback: the cached choice is kept intact (if the
          // catalog offers the font again, it re-applies by itself) — so its
          // display name is still there for the notice; fall back to the id.
          const sel = selection();
          return sel && sel.id === desiredFontId() ? sel.name : desiredFontId();
        },
        fontFamilyName: () => effectiveName(),
        brandLabel: BRAND_FONT.label,
        catalog: FONT_CATALOG,
        setFont,
        resetFont,
      }}
    >
      {props.children}
    </TypographyContext.Provider>
  );
};

export const useTypography = (): TypographyContextValue => {
  const ctx = useContext(TypographyContext);
  if (!ctx) {
    throw new Error("useTypography must be used within TypographyProvider");
  }
  return ctx;
};
